import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContentText,
  DialogActions,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTheme } from '@mui/material/styles';

import { useTranslation } from '../../context/TranslationContext';
import { StyledCloseIcon } from '../../constants/styledComponents';

const DialogBase = ({
  title,
  subtitle,
  isOpen,
  onClose,
  onConfirm,
  icon,
  bgColor,
  confirmLabel,
  confirmButtonColor = 'primary',
  confirmIcon,
  inProgress = false,
  confirmDisabled = false,
  dialogAbortId,
  warningMessage,
  hideCancelButton = false,
  cancelLabel,
  cancelIcon,
  cancelButtonColor,
  cancelButtonVariant = 'outlined',
  hideCloseIcon = false,
  showRepeat = false,
  onRepeat,
  activeSession = -1,
  repeatDisabled = false,
}) => {
  const theme = useTheme();
  const { translate } = useTranslation();
  return (
    <Dialog
      fullWidth
      open={isOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose();
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      disableEnforceFocus
      slotProps={{
        paper: {
          sx: {
            overflow: 'visible',
            borderRadius: '14px',
            maxWidth: '530px',
            width: '100%',
          },
        },
      }}
    >
      {!inProgress && !hideCloseIcon && (
        <StyledCloseIcon
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            cursor: 'pointer',
          }}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '-35px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: bgColor,
            color: theme.palette.background.common,
            borderRadius: '50%',
            width: '100px',
            height: '100px',
            border: '10px solid',
            borderColor: theme.palette.background.common,
          }}
        >
          {inProgress ? <CircularProgress color="inherit" size={40} /> : icon}
        </Box>
      </Box>
      <Typography
        variant="h5"
        align="center"
        style={{ marginBottom: '5px', paddingTop: 10, paddingBottom: 8 }}
      >
        {title}
      </Typography>
      <DialogContentText id="alert-dialog-description" component="div">
        <Typography
          variant="body1"
          align="center"
          sx={{
            mb: warningMessage ? 0 : 3,
            pl: 1,
            pr: 1,
          }}
        >
          {subtitle}
        </Typography>
        {warningMessage ? (
          <Alert
            severity="warning"
            sx={{
              justifyContent: 'center',
              m: 1,
              width: 'auto',
              '& .MuiAlert-icon': {
                marginRight: 1,
              },
              '& .MuiAlert-message': {
                flexGrow: 0,
                textAlign: 'center',
              },
            }}
          >
            <Typography variant="body2" align="center">
              {warningMessage}
            </Typography>
          </Alert>
        ) : null}
      </DialogContentText>
      <DialogActions sx={{ display: 'flex', width: '100%' }}>
        {!inProgress && !hideCancelButton && (
          <Button
            variant={cancelButtonVariant}
            color={cancelButtonColor}
            onClick={onClose}
            startIcon={cancelIcon !== undefined ? cancelIcon : <CancelIcon />}
          >
            {cancelLabel || translate('cancel')}
          </Button>
        )}
        {showRepeat && activeSession > 0 ? (
          <Button
            variant="outlined"
            color="primary"
            onClick={onRepeat}
            sx={{ ml: hideCancelButton ? 0 : 1, mr: 1 }}
            disabled={repeatDisabled}
          >
            {translate('abortDialog.repeatPart')}
          </Button>
        ) : null}
        {!inProgress && (
          <Button
            variant="contained"
            onClick={!confirmDisabled ? onConfirm : undefined}
            color={confirmButtonColor}
            startIcon={confirmIcon ? confirmIcon : null}
            id={dialogAbortId}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DialogBase;
